// Column cell width — SAME in both zooms (a Daily 6-hour cell is as wide as a Monthly
// day cell). Daily just shows 4 columns per day → a day is 4× wider. See timeScale.ts.
export const DAY_WIDTH = 105;
export const ROW_HEIGHT = 44;
export const HEADER_MONTH_HEIGHT = 36;
export const HEADER_DAY_HEIGHT = 72;
export const HEADER_HEIGHT = HEADER_MONTH_HEIGHT + HEADER_DAY_HEIGHT;
// Plate sub-column width (always shown). The TOTAL sticky-left width is dynamic:
// `leftWidth = PLATE_COL_WIDTH + extraColumns * TIMELINE_COL_WIDTH` — computed in
// VehicleTimeline and threaded to the canvas/header/today-line (which all key off
// "where the day area starts"). Keep using the constant only for the plate cell.
export const PLATE_COL_WIDTH = 150;
// Width of each optional left column (Status/Area/Fleet/Model/VIN) added via the
// Columns selector, rendered to the right of the plate in the sticky-left area.
export const TIMELINE_COL_WIDTH = 120;
// Drag-to-resize bounds for the sticky-left columns (PLATE_COL_WIDTH / TIMELINE_COL_WIDTH
// are the DEFAULTS; user-dragged widths are per-session, clamped to these).
export const MIN_PLATE_COL_WIDTH = 90;
export const MIN_TIMELINE_COL_WIDTH = 60;
export const MAX_COL_WIDTH = 480;

// ~3 months back (user request). Doubles as the status-history fetch `from` and
// the jump-to-date picker's lower bound (both key off this constant).
export const DAYS_BEFORE_TODAY = 90;
// ~6 months ahead (user request).
export const DAYS_AFTER_TODAY = 180;

// Day cells visible to the LEFT of today/the focused day when scrolling to it.
// 1 = yesterday sits at the day-area's left edge, today is the 2nd visible column.
export const DAYS_BEFORE_FOCUS = 1;

// Group expand/collapse animation duration (ms). Used by both the plate cells
// and the body rows so they animate in lockstep.
export const COLLAPSE_MS = 220;

// Booking tile (canvas) geometry + colours.
// Tile fills are the OLD timeline's solid colours (OutdatedVehicleTimeTable):
//   booking (confirmed/offered) → olive; trip statuses → lime.
// NOTE: the old timeline also tinted by `requestedStatus` via getStatusBG, but
// that returns CSS gradient strings which a canvas `fillStyle` can't render, and
// status-period shading is a later iteration — so tiles use solid fills only.
export const EVENT_HEIGHT = 28; // pill height inside the 44px row
export const TILE_RADIUS = 6;
export const TILE_INSET = 2; // trailing gap so back-to-back tiles don't touch
// Tiles are positioned at 15-minute resolution (not whole days) so several
// bookings can sit within one day cell. A floor: very short bookings still need
// to be visible/clickable, so clamp the rendered width to this minimum.
export const MIN_TILE_WIDTH = 4;
// Gap kept between a scroll-tracking label and the viewport edge it rides: the
// presentable-id label from the sticky plate column's right edge, the end-date
// label from the viewport's right edge — so neither sits flush against the edge.
export const TILE_EDGE_PAD = 20;
export const TILE_BOOKING_BG = '#a2ad00';
export const TILE_TRIP_BG = '#d8f52b';
export const TILE_BOOKING_TEXT = '#FFFFFF';
export const TILE_TRIP_TEXT = '#2B2E42';

// Status-change period backgrounds (drawn on the canvas behind the booking tiles,
// over the full row height for the period's from→to span). Mirrors ExampleTimeline's
// striped pills (`createUnavailablePattern`): a 24px tile of alternating 12px diagonal
// bands of TWO close tones (a candy-stripe, `repeating-linear-gradient(-45deg,…)`).
// RELEASE_READY uses the example's slate pair verbatim; BLOCKED uses a soft-red pair.
// `c1` = the stripe band, `c2` = the background. Everything else → no band.
export const STATUS_STRIPE_WIDTH = 12; // px per diagonal band (verbatim from the example)
export const STATUS_STRIPE = {
  blocked: { c1: '#FCDCDC', c2: '#F7C9C9' },
  releaseReady: { c1: '#F1F5F9', c2: '#E2E8F0' },
} as const;
// Opacity applied to ALL status bands while a booking/trip drawer is open (so the
// selected tile stands out and the rest of the row de-emphasises).
export const STATUS_BAND_DIM_ALPHA = 0.4;
export const TILE_BORDER = 'rgba(0, 0, 0, 0.12)';
// Dimmed (greyed) tile colours — used for every tile EXCEPT the open/hovered one
// when a booking/trip drawer is open (de-emphasis without a backdrop).
export const TILE_DIM_BG = '#D8DCE3';
export const TILE_DIM_TEXT = '#8A93A3';
// Hover highlight — a small STATIC upward lift (px) + deeper shadow. A fixed lift
// (not a scale) keeps the effect identical on short and long bookings; a width
// proportional scale made long tiles "jump" too far.
export const TILE_HOVER_LIFT = 2;
export const TILE_LABEL_SIZE = 12; // presentable id
export const TILE_LABEL_WEIGHT = 600;
export const TILE_END_SIZE = 11; // sliding end-date label
export const TILE_END_WEIGHT = 500;
export const TILE_FONT_FAMILY =
  '-apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Pulled VERBATIM from `ExampleTimeline/timeline-gantt/config/timeline-constants.ts`
// TIMELINE_COLORS. Do NOT soften, lighten, or otherwise reinterpret these values
// without the user explicitly asking — see CLAUDE.md "Match the example exactly".
//
// Example source values:
//   GRID_BACKGROUND:        '#FFFFFF'   ← day cells + body
//   GRID_BORDER:            '#CAD5E2'   ← ALL grid / header / cell borders
//   HEADER_BACKGROUND:      '#F8FAFC'   ← month row background
//   HEADER_TEXT:            '#000000'
//   HEADER_TODAY_BACKGROUND:'#F5F3FF'   ← today day-cell fill (light tint of accent)
//   HEADER_TODAY_ACCENT:    '#7F22FE'   ← top border + vertical line accent
// `text-primaryText` resolves to `#2B2E42` (g-admin brand navy, also at
// `ViewTypeSwitch.styles.ts:21`). User requested navy in place of the example's purple.
export const COLORS = {
  // All cell borders share ONE colour. Example uses `GRID_BORDER` everywhere.
  gridBorder: '#CAD5E2',
  // Aliases kept so call sites read naturally; ALL point at the same `GRID_BORDER`.
  gridLineVertical: '#CAD5E2',
  gridLineHorizontal: '#CAD5E2',
  monthRowBorder: '#CAD5E2',
  headerBorder: '#CAD5E2',
  plateColumnBorder: '#CAD5E2',

  monthRowBg: '#F8FAFC',
  headerBg: '#FFFFFF',
  // HEADER_TEXT verbatim from example — pure black, shared by weekday + number.
  headerText: '#000000',
  headerSubtleText: '#94A3B8',

  // Plate column right-edge shadow — adds visual depth so the sticky column reads
  // as floating above the scrollable body. (Not in example source as a `shadow` constant
  // but matches the example's visual treatment per user request.)
  plateColumnShadow: '4px 0 8px -4px rgba(15, 23, 42, 0.12)',

  // Area-group row bg — slightly darker than body cell bg (#F8FAFC) so the area
  // header row reads as a distinct band across the timeline. User-specified value.
  groupRowBg: 'rgb(242, 245, 249)',

  // text-primaryText — brand navy. Used for month label + today text + today accent.
  // (Recolour of the example's `#7F22FE` purple per explicit user request.)
  primaryText: '#2B2E42',
  monthLabelText: '#2B2E42',
  todayAccent: '#2B2E42',
  todayText: '#2B2E42',
  // Faint navy tint for the today day-cell fill (mirrors `HEADER_TODAY_BACKGROUND`).
  todayCellBg: 'rgba(43, 46, 66, 0.06)',

  plateColumnBg: '#FFFFFF',
  plateText: '#0F172A',
  plateSubtitle: '#64748B',
};

export const getStatusBG = (status: string | undefined): string => {
  switch (status) {
    case 'BLOCKED':
      return '#ee4e4e60';
    case 'RELEASED':
      return '#fdfdfd';
    case 'RENTED':
      return '#fdfdfd';
    case 'RELEASE_READY':
    case 'EQUIPMENT_READY':
    case 'EQUIPPED':
      return 'repeating-linear-gradient(45deg,#A0A0A010,#A0A0A010 10px,#A0A0A040 10px, #A0A0A040 20px)';
    case 'TERMINATED':
      return 'repeating-linear-gradient(45deg,#A0A0A0,#A0A0A0 10px,#A0A0A040 10px, #A0A0A040 20px)';
    case 'REMOVED':
      return '#A0A0A080';
    default:
      return 'transparent';
  }
};
